import React, { useState } from "react";
import { ListPlus, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useCreateCallSummaryTaskMutation } from "@/store/rest_api";
import type { CallSummary } from "@/model/interface/CallRecording";
import { apiErrorMessage } from "@/util/apiError";
import { Button } from "@/components/ui/button";

interface CallSummaryBlockProps {
  callId: string;
  summary: CallSummary;
}

export default function CallSummaryBlock({ callId, summary }: CallSummaryBlockProps) {
  const [createTask, { isLoading: isCreatingTask }] = useCreateCallSummaryTaskMutation();
  const [isTaskCreated, setIsTaskCreated] = useState(Boolean(summary.suggestedTaskId));

  const handleCreateTask = async () => {
    try {
      await createTask(callId).unwrap();
      setIsTaskCreated(true);
      toast.success("Task created");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not create the task"));
    }
  };

  return (
    <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3 text-sm">
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        <Sparkles className="h-3.5 w-3.5" />
        Summary
      </div>
      <p>{summary.summary}</p>
      {summary.keyPoints.length > 0 && (
        <ul className="list-disc space-y-0.5 pl-5">
          {summary.keyPoints.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
      )}
      {summary.nextStep && (
        <p>
          <span className="font-medium">Next step:</span> {summary.nextStep}
        </p>
      )}
      {summary.suggestedTask &&
        (isTaskCreated ? (
          <p className="text-muted-foreground">Task created: {summary.suggestedTask}</p>
        ) : (
          <Button size="sm" variant="outline" onClick={() => void handleCreateTask()} disabled={isCreatingTask}>
            {isCreatingTask ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ListPlus className="mr-2 h-4 w-4" />}
            Create task: {summary.suggestedTask}
          </Button>
        ))}
    </div>
  );
}
